import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import * as dh from '../../helpers/dates';
import * as eh from '../../helpers/event-helper';
import { useEventCallback } from '../../helpers/hooks';
import * as utils from '../../helpers/mobile';
import {
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TableTotalCell from './TableTotalCell';
import TableCell from './TableCell';
import RampServiceTable from './RampServiceTable';
import TableRowPlaceholder from './TableRowPlaceholder';
import { getPaxLoadSummary } from '../../helpers/event-helper';
import styles from './styles';

const useStyles = makeStyles(styles);

const OutboundStatsTable = ({ events, rampServices }) => {
  const classes = useStyles();
  const [selectedRow, setSelectedRow] = useState(null);
  const isPortrait = utils.isPortrait();

  const total = useMemo(() => {
    return events.reduce(
      (result, event) => {
        const { outbound } = event;
        result.bags += eh.getTotalBagsCount(outbound.actualPaxLoad);
        result.pax += eh.getTotalPaxLoad(
          getPaxLoadSummary(event.outbound.actualPaxLoad)
        );
        result.transfers += eh.getTransferCount(outbound.transfers);
        result.cargoWeight += eh.getTotalCargoWeight(outbound.cargo);
        return result;
      },
      {
        pax: 0,
        transfers: 0,
        cargoWeight: 0,
        bags: 0,
      }
    );
  }, [events]);

  const handleRowClick = useEventCallback(event => {
    setSelectedRow(event.currentTarget.getAttribute('data-eventid'));
  });

  return (
    <>
      <Table
        size="small"
        stickyHeader
        className={classNames(classes.leftTable, {
          [classes.fixedTable]: !isPortrait,
        })}
      >
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCell>Date/STD</TableCell>
            <TableCell>Airline</TableCell>
            <TableCell>Flight Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!events.length && <TableRowPlaceholder />}
          {events.map(event => {
            return (
              <TableRow
                selected={event._id === selectedRow}
                key={event._id}
                data-eventid={event._id}
                onClick={handleRowClick}
              >
                <TableCell>{dh.dateToDateTimeString(event.STD)}</TableCell>
                <TableCell>{event.airline}</TableCell>
                <TableCell
                  className={classes.flightNumberCell}
                  data-eventid={event._id}
                >
                  <Link to={`events/${event._id}`}>{event.outboundNumber}</Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableTotalCell colSpan={4}>
              Total Rows: {events.length}
            </TableTotalCell>
          </TableRow>
        </TableFooter>
      </Table>
      <Table className={classes.mainTable} size="small" stickyHeader>
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCell>Date/ATD</TableCell>
            <TableCell>DL Time</TableCell>
            <TableCell>Route</TableCell>
            <TableCell>Aircraft Type</TableCell>
            <TableCell>Aircraft Registration</TableCell>
            <TableCell>Ops. crew</TableCell>
            <TableCell>DHC</TableCell>
            <TableCell>Total Pax</TableCell>
            <TableCell>Transfer Pax</TableCell>
            <TableCell>Transit Pax</TableCell>
            <TableCell>Pax Weight (kg)</TableCell>
            <TableCell>Total Bags</TableCell>
            <TableCell>Bag Weight (kg)</TableCell>
            <TableCell>Handbag Weight (kg)</TableCell>
            <TableCell>Cargo (kg)</TableCell>
            <TableCell>Payload (kg)</TableCell>
            <TableCell>Underload (kg)</TableCell>
            <TableCell>Check-in Counters</TableCell>
            <TableCell>Gate</TableCell>
            <TableCell>Parking no.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!events.length && <TableRowPlaceholder />}
          {events.map(event => {
            let [isDelayed, delayTime] = eh.calculateDelayTime(
              event.STD,
              event.outbound.ATD || event.outbound.ETD
            );
            const isAheadOfTime = delayTime.startsWith('-');
            if (delayTime === '00:00') delayTime = null;

            const {
              paxWeight,
              bagsWeight,
              handbagsWeight,
              cargoWeight,
              payload,
            } = eh.getPayload(
              event.outbound.ATD,
              event.outbound.actualPaxLoad,
              event.outbound.cargo
            );

            const totalBagsCount = eh.getTotalBagsCount(
              event.outbound.actualPaxLoad
            );

            return (
              <TableRow
                selected={event._id === selectedRow}
                key={event._id}
                data-eventid={event._id}
                onClick={handleRowClick}
              >
                <TableCell noLeftBorder>
                  {dh.dateToDateTimeString(event.outbound.ATD)}
                </TableCell>
                <TableCell
                  className={classNames({
                    [classes.aheadOfTime]: isAheadOfTime,
                    [classes.delayed]: isDelayed,
                  })}
                >
                  {delayTime}
                </TableCell>
                <TableCell>{`${event.airport}–${event.outboundAirport}`}</TableCell>
                <TableCell>{event.outbound.aircraftType}</TableCell>
                <TableCell>{event.outbound.aircraftReg}</TableCell>
                <TableCell>{eh.getOpsCrewCount(event.outbound.crew)}</TableCell>
                <TableCell>
                  {eh.getDeadHeadCrewCount(event.outbound.crew)}
                </TableCell>
                <TableCell>
                  {eh.getTotalPaxLoad(
                    eh.getPaxLoadSummary(event.outbound.actualPaxLoad)
                  )}
                </TableCell>
                <TableCell>
                  {eh.getTransferCount(event.outbound.transfers)}
                </TableCell>
                <TableCell />
                <TableCell>{paxWeight}</TableCell>
                <TableCell>{totalBagsCount}</TableCell>
                <TableCell>{bagsWeight}</TableCell>
                <TableCell>{handbagsWeight}</TableCell>
                <TableCell>{cargoWeight}</TableCell>
                <TableCell>{payload}</TableCell>
                <TableCell />
                <TableCell>{event.outbound.checkInCounters}</TableCell>
                <TableCell>{event.outbound.gateNo}</TableCell>
                <TableCell>{event.outbound.parkingBay}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell>{total.pax}</TableTotalCell>
            <TableTotalCell>{total.transfers}</TableTotalCell>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell>{total.bags}</TableTotalCell>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell>{total.cargoWeight}</TableTotalCell>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell />
          </TableRow>
        </TableFooter>
      </Table>
      {rampServices.map(rampService => (
        <RampServiceTable
          key={rampService.name}
          rampService={rampService}
          events={events}
          selectedRow={selectedRow}
          onRowClick={handleRowClick}
        />
      ))}
    </>
  );
};

export default OutboundStatsTable;
